/**
 * Consentimento de cookies de análise e marketing — a escolha do banner
 * (`CookieConsent`) e as regras que o PostHog segue a partir dela.
 *
 * - **Site público** (home, LPs, ferramentas, blog, páginas jurídicas): sem
 *   "Aceitar", o PostHog não envia nada. É o que a Política de Privacidade
 *   promete ("análise só se você aceitar").
 * - **Login e plataforma logada**: o banner não aparece ali, então a medição
 *   segue, por execução de contrato e legítimo interesse (diagnóstico de
 *   login, uso da plataforma) — mas sem cookie nem localStorage de análise
 *   enquanto não houver aceite: o PostHog guarda o id só na memória da aba.
 *
 * O `index.html` lê a mesma chave para o Consent Mode do GTM antes do React
 * carregar; se ela mudar, mude lá também.
 */

export const CONSENT_STORAGE_KEY = "fidexa_cookie_consent";

export type ConsentValue = "granted" | "denied";

export const readConsent = (): ConsentValue | null => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    // localStorage bloqueado (modo anônimo restrito): sem escolha registrada.
    return null;
  }
};

export const hasAnalyticsConsent = () => readConsent() === "granted";

// Prefixos da área logada e do fluxo de login. Lista de exclusão de propósito:
// rota pública nova (ou desconhecida) já nasce exigindo consentimento.
const APP_PREFIXES = [
  "/auth",
  "/login",
  "/register",
  "/dashboard",
  "/chat",
  "/hub",
  "/v2",
  "/notificacoes",
];

export const isAppPath = (pathname: string) =>
  APP_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

/** Persistência do PostHog: cookie/localStorage só com aceite. */
export const posthogPersistence = () =>
  hasAnalyticsConsent() ? "localStorage+cookie" : "memory";

/**
 * Apaga o que o PostHog gravou em visitas anteriores a esta regra (cookie
 * `ph_*` e chaves `ph_*` do localStorage). Roda quando não há aceite: sem
 * isso, quem nunca aceitou continuaria com o identificador antigo gravado.
 */
export const clearPosthogStorage = () => {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith("ph_"))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    // localStorage bloqueado: não há o que apagar.
  }

  // O cookie do PostHog vale para o domínio raiz (".fidexa.com.br"); apagar
  // exige repetir o mesmo domínio, então tentamos o host e cada sufixo dele.
  const labels = window.location.hostname.split(".");
  const domains = labels.map((_, index) => labels.slice(index).join("."));
  document.cookie
    .split(";")
    .map(cookie => cookie.split("=")[0].trim())
    .filter(name => name.startsWith("ph_"))
    .forEach(name => {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      domains.forEach(domain => {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
      });
    });
};
